import React from 'react';

const ListingImages = ({ 
  images, 
  firstImageUrl, 
  categoryName, 
  activeImageIndex, 
  onImageChange,
  imageBaseUrl,
  title 
}) => {
  const getCategoryIcon = () => {
    if (!categoryName) return '📦';
    
    const category = categoryName.toLowerCase();
    if (category.includes('phone') || category.includes('electronic')) return '📱';
    if (category.includes('car') || category.includes('vehicle')) return '🚗';
    if (category.includes('property') || category.includes('house')) return '🏠';
    if (category.includes('service')) return '🔧';
    if (category.includes('fashion') || category.includes('cloth')) return '👕';
    if (category.includes('food')) return '🍕';
    if (category.includes('book')) return '📚';
    if (category.includes('sport')) return '⚽';
    
    return '📦';
  };

  const renderMainImage = () => {
    if (images && images.length > 0) {
      const activeImage = images[activeImageIndex];
      return (
        <img 
          src={`${imageBaseUrl}${activeImage.image_url}`}
          alt={`${title} - Image ${activeImageIndex + 1}`}
          className="w-full h-full object-cover rounded-xl transition-transform duration-500 hover:scale-105"
          onError={(e) => {
            console.log('Image failed to load:', activeImage.image_url);
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    } else if (firstImageUrl) {
      return (
        <img 
          src={`${imageBaseUrl}${firstImageUrl}`}
          alt={title}
          className="w-full h-full object-cover rounded-xl transition-transform duration-500 hover:scale-105"
          onError={(e) => {
            console.log('Image failed to load:', firstImageUrl);
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    
    return null;
  };

  const renderThumbnails = () => {
    if (!images || images.length === 0) return null;

    return (
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mt-4">
        {images.map((image, index) => (
          <button
            key={image.image_id}
            onClick={() => onImageChange(index)}
            className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-200 hover:scale-105 ${
              index === activeImageIndex 
                ? 'border-[#ff660e] ring-2 ring-[#ff660e]/30 transform scale-105' 
                : 'border-gray-600 hover:border-gray-500'
            }`}
          >
            {image.image_url ? (
              <img 
                src={`${imageBaseUrl}${image.image_url}`}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                <span className="text-gray-400 text-sm">{index + 1}</span>
              </div>
            )}
          </button>
        ))}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="w-full h-96 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center group">
      <div className="text-center">
        <div className="text-6xl text-gray-600 group-hover:text-[#ff660e] transition-colors mb-4">
          {getCategoryIcon()}
        </div>
        <h3 className="text-lg font-medium text-gray-400">No images available</h3>
        <p className="text-gray-500 text-sm mt-1">This listing doesn't have any images</p>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-800 rounded-2xl shadow-lg border border-gray-700 p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">
          Product Images {images && images.length > 0 ? `(${images.length})` : ''}
        </h3>
        {images && images.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span className="font-medium">{activeImageIndex + 1}</span>
            <span>/</span>
            <span className="text-gray-300">{images.length}</span>
          </div>
        )}
      </div>
      
      {images && images.length > 0 ? (
        <>
          <div className="relative h-96 rounded-xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 group">
            {renderMainImage()}
            <div className="w-full h-full hidden items-center justify-center">
              <div className="text-center">
                <div className="text-6xl text-gray-600 mb-4">{getCategoryIcon()}</div>
                <p className="text-lg text-gray-400">Image {activeImageIndex + 1}</p>
              </div>
            </div>
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => onImageChange((activeImageIndex - 1 + images.length) % images.length)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gray-800/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-[#ff660e] hover:scale-110 transition-all duration-200 border border-gray-700"
                >
                  <span className="text-white hover:text-white">←</span>
                </button>
                <button
                  onClick={() => onImageChange((activeImageIndex + 1) % images.length)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gray-800/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-[#ff660e] hover:scale-110 transition-all duration-200 border border-gray-700"
                >
                  <span className="text-white hover:text-white">→</span>
                </button>
              </>
            )}
          </div>
          {renderThumbnails()}
        </>
      ) : firstImageUrl ? (
        <div className="relative h-96 rounded-xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
          {renderMainImage()}
          <div className="w-full h-full hidden items-center justify-center">
            <div className="text-center">
              <div className="text-6xl text-gray-600 mb-4">{getCategoryIcon()}</div>
              <p className="text-lg text-gray-400">Product Image</p>
            </div>
          </div>
        </div>
      ) : (
        renderEmptyState()
      )}
    </div>
  );
};

export default ListingImages;